import { logger } from './logger';

export interface UsageLimitError {
  type: 'usage_limit';
  endpoint: string;
  limit: number;
  used: number;
  period: 'daily' | 'monthly';
  retryAfter?: number;
}

export interface RateLimitError {
  type: 'rate_limit';
  retryAfter: number;
  message: string;
}

export interface NetworkError {
  type: 'network_error';
  code: 'CONNECTION_FAILED' | 'TIMEOUT' | 'OFFLINE' | 'SERVER_DOWN' | 'DNS_FAILED';
  message: string;
  retryable: boolean;
}

export interface BackendError {
  type: 'backend_error';
  status: number;
  message: string;
  retryable: boolean;
}

export type ApiError = UsageLimitError | RateLimitError | NetworkError | BackendError | { type: 'other'; message: string };

export function parseApiError(error: string, _originalError?: any): ApiError {
  // Check for network connectivity errors
  if (error.includes('Network error') || error.includes('Failed to fetch') || error.includes('fetch')) {
    if (error.includes('timeout') || error.includes('TIMEOUT')) {
      return {
        type: 'network_error',
        code: 'TIMEOUT',
        message: 'The request timed out. Please check your internet connection and try again.',
        retryable: true
      };
    }

    if (error.includes('offline') || error.includes('OFFLINE')) {
      return {
        type: 'network_error',
        code: 'OFFLINE',
        message: 'You appear to be offline. Please check your internet connection and try again.',
        retryable: true
      };
    }

    if (error.includes('DNS') || error.includes('dns')) {
      return {
        type: 'network_error',
        code: 'DNS_FAILED',
        message: 'Unable to connect to our servers. Please check your internet connection and try again.',
        retryable: true
      };
    }

    return {
      type: 'network_error',
      code: 'CONNECTION_FAILED',
      message: 'Unable to connect to our servers. Please check your internet connection and try again.',
      retryable: true
    };
  }

  // Check for HTTP status codes that indicate server issues
  if (error.includes('HTTP 5')) {
    const statusMatch = error.match(/HTTP (\d+)/);
    const status = statusMatch ? parseInt(statusMatch[1]) : 500;

    return {
      type: 'backend_error',
      status,
      message: 'Our servers are experiencing issues. Please try again in a few moments.',
      retryable: true
    };
  }

  if (error.includes('HTTP 4')) {
    const statusMatch = error.match(/HTTP (\d+)/);
    const status = statusMatch ? parseInt(statusMatch[1]) : 400;

    return {
      type: 'backend_error',
      status,
      message: 'There was an issue with your request. Please try again.',
      retryable: status >= 500
    };
  }

  // Check for usage limit errors
  if (error.includes('limit exceeded')) {
    const endpointMatch = error.match(/for (\w+)/);
    const limitMatch = error.match(/(\d+)\/(\d+)/);
    const periodMatch = error.match(/(daily|monthly)/);

    if (endpointMatch && limitMatch && periodMatch) {
      return {
        type: 'usage_limit',
        endpoint: endpointMatch[1],
        used: parseInt(limitMatch[1]),
        limit: parseInt(limitMatch[2]),
        period: periodMatch[1] as 'daily' | 'monthly'
      };
    }
  }

  // Check for rate limit errors
  if (error.includes('Rate limit exceeded')) {
    const retryMatch = error.match(/(\d+)/);
    return {
      type: 'rate_limit',
      retryAfter: retryMatch ? parseInt(retryMatch[1]) : 60,
      message: error
    };
  }

  // Default to other error
  return {
    type: 'other',
    message: error
  };
}

export function getEndpointDisplayName(endpoint: string): string {
  const endpointNames: Record<string, string> = {
    'analyzeSleepPatterns': 'Sleep Analysis',
    'generateSleepGoals': 'Goal Generation',

    'generateSuggestedPrompts': 'Suggested Prompts'
  };

  return endpointNames[endpoint] || endpoint;
}

export function formatTimeRemaining(seconds: number): string {
  if (seconds < 60) {
    return `${seconds} seconds`;
  } else if (seconds < 3600) {
    const minutes = Math.ceil(seconds / 60);
    return `${minutes} minute${minutes > 1 ? 's' : ''}`;
  } else {
    const hours = Math.ceil(seconds / 3600);
    return `${hours} hour${hours > 1 ? 's' : ''}`;
  }
}

export function showUsageLimitError(error: UsageLimitError, toast: any) {
  const endpointName = getEndpointDisplayName(error.endpoint);
  const periodText = error.period === 'daily' ? 'today' : 'this month';

  toast({
    variant: 'destructive',
    title: 'Usage Limit Reached',
    description: `You've used ${error.used}/${error.limit} ${endpointName} requests ${periodText}. Please try again later.`,
  });
}

export function showRateLimitError(error: RateLimitError, toast: any) {
  const timeRemaining = formatTimeRemaining(error.retryAfter);

  toast({
    variant: 'destructive',
    title: 'Too Many Requests',
    description: `Please wait ${timeRemaining} before trying again.`,
  });
}

export function showNetworkError(error: NetworkError, toast: any) {
  const titles = {
    'CONNECTION_FAILED': 'Connection Failed',
    'TIMEOUT': 'Request Timed Out',
    'OFFLINE': 'You\'re Offline',
    'SERVER_DOWN': 'Server Unavailable',
    'DNS_FAILED': 'Connection Failed'
  };

  toast({
    variant: 'destructive',
    title: titles[error.code] || 'Network Error',
    description: error.message,
    action: error.retryable ? {
      label: 'Try Again',
      onClick: () => window.location.reload()
    } : undefined
  });
}

export function showBackendError(error: BackendError, toast: any) {
  const titles = {
    500: 'Server Error',
    502: 'Service Unavailable',
    503: 'Service Unavailable',
    504: 'Gateway Timeout'
  };

  toast({
    variant: 'destructive',
    title: titles[error.status as keyof typeof titles] || 'Server Error',
    description: error.message,
    action: error.retryable ? {
      label: 'Retry',
      onClick: () => window.location.reload()
    } : undefined
  });
}

export function showGenericError(error: string, toast: any) {
  toast({
    variant: 'destructive',
    title: 'Error',
    description: error || 'Something went wrong. Please try again.',
  });
}

export function handleApiError(error: string, toast: any, _originalError?: any) {
  const parsedError = parseApiError(error, _originalError);

  // Log the error
  const logMessage = 'message' in parsedError ? parsedError.message : `Error: ${parsedError.type}`;
  logger.error(logMessage, {
    type: parsedError.type,
    originalError: error,
    details: parsedError
  });

  switch (parsedError.type) {
    case 'usage_limit':
      showUsageLimitError(parsedError, toast);
      break;
    case 'rate_limit':
      showRateLimitError(parsedError, toast);
      break;
    case 'network_error':
      showNetworkError(parsedError, toast);
      break;
    case 'backend_error':
      showBackendError(parsedError, toast);
      break;
    case 'other':
      showGenericError(parsedError.message, toast);
      break;
  }
}
